import { AuthPermission } from '../../manage-users/models/permission';
import { PaginatedResponse } from '../../../shared/interfaces/pagination';

export interface CreateCheckoutSessionResponse {
  checkout_url: string;
  session_id: string;
}

export interface SubscriptionDetails {
  plan_name?: string;
  status?: string;
  current_period_start?: string;
  current_period_end?: string;
  cancel_at_period_end?: boolean;
  billing_interval?: string;
  pending_plan?: string;
  pending_plan_display_name?: string;
  display_name?: string;
  amount?: number;
  webhook_status?: string;
  quantity?: number;
  trial_end?: string | null;
  is_trialing?: boolean;
}

export interface TransactionHistory {
  invoice_id: string;
  invoice_number: string;
  amount: number;
  currency: string;
  status: string;
  created_at: string;
  receipt_url: string;
  invoice_pdf: string;
}

export interface CursorPaginatedResponse<T> {
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface SwitchPlan {
  target_plan: string;
  billing_interval: 'month' | 'year';
}

export interface SubscriptionPlanPrice {
  amount: number;
  billing_interval: 'month' | 'year';
}

export interface SubscriptionPlanMenu {
  plan_name: string;
  display_name: string;
  is_active: boolean;
  is_custom_plan: boolean;
  marketing_features: string[];
  prices: SubscriptionPlanPrice[];
}

export interface SubscriptionCustomerPortalSession {
  url: string;
}

export enum CustomerPortalFlow {
  Billing = 'billing',
  SwitchPlan = 'subscription_update',
  CancelSubscription = 'subscription_cancel',
}

export interface CustomSubscriptionPlansSummary {
  total: number;
  active: number;
  archived: number;
}

export interface CustomSubscriptionPlanListResponse
  extends PaginatedResponse<CustomSubscriptionPlanListItem> {
  summary: CustomSubscriptionPlansSummary;
}

export interface CustomSubscriptionPlanListItem {
  id: number;
  plan_name: string;
  display_name: string;
  is_active: boolean;
  is_custom_plan: boolean;
  trial_period_days: number;
  created_at: string;
  updated_at: string;
  connections_count: number;
  subscribed_connections_count: number;
  features_count: number;
}

export interface CustomSubscriptionPlan {
  id: number;
  plan_name: string;
  display_name: string;
  marketing_features: (string | MarketingFeatures)[];
  is_active: boolean;
  is_custom_plan: boolean;
  trial_period_days: number;
  created_at: string;
  updated_at: string;

  prices: CustomSubPlanPrice[];
  connections: CustomSubConnection[];
  permissions: SubscriptionPermission[];
  active_subscriptions?: ActiveSubscriptionsForPlan[];
}

export interface CustomSubPlanPrice {
  id: number;
  amount: number;
  billing_interval: 'month' | 'year';
  is_active: boolean;
}

export interface CustomSubConnection {
  connection_id: number;
  connection_name: string;
  display_name: string;
  subscription_admin_user_id?: number | null;
  subscription_admin_name?: string | null;
  subscription_admin_email?: string | null;
  subscription_admins?: ConnectionUser[];
  subscription_status?: string | null;
  active_plan_name?: string | null;
}

export interface PlanConnectionPayload {
  connection_name: string;
  subscription_admin_user_id?: number | null;
  subscription_admin_user_ids?: number[];
}

export interface SubscriptionPermission {
  id: number;
  name: string;
  codename: string;
}

export type SubscriptionPlanFilter = 'all' | 'active' | 'archive';

export interface MarketingFeatures {
  name: string;
}
export interface CreateSubscriptionForm {
  plan_name: string;
  display_name: string;
  marketing_features: MarketingFeatures[];
  month_price: number | null;
  year_price: number | null;
  connections: PlanConnectionPayload[];
  permissions: number[];
  trial_period_days: number;
}

export interface UpdateSubscriptionPlanDetails {
  display_name?: string;
  connections?: PlanConnectionPayload[];
  is_active?: boolean;
  trial_period_days?: number;
  permissions?: number[];
}

export interface StartTrialPayload {
  plan_name: string;
  connection_name: string;
}

export interface SubscriptionConnectionListItem {
  connection_id: number;
  connection_name: string;
  connection_display_name: string;
  subscription_admins: ConnectionUser[];
}

export interface ConnectionUser {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
}

export interface ConnectionSubscriptionDetails extends SubscriptionDetails {
  subscription_admin_name?: string | null;
  subscription_admin_email?: string | null;
  subscription_admins?: ConnectionUser[];
}

export interface ActiveSubscriptionsForPlan {
  id: number;
  plan_name: string;
  status: string;
  current_period_start?: string;
  current_period_end?: string;
  connection_name: string;
  connection_display_name?: string;
  stripe_customer_dashboard_url?: string | null;
  connection_id?: number | null;
  subscription_admin_name?: string | null;
  subscription_admin_email?: string | null;
  subscription_admins?: ConnectionUser[];
  trial_end?: string | number | null;
}

interface CreateFormConfigType {
  plan_name: {
    value: string;
    validators: Record<string, string>;
    error: null | string;
  };
  display_name: {
    value: string;
    validators: Record<string, string>;
    error: null | string;
  };
  month_price: {
    value: number | null;
    validators: Record<string, string>;
    error: null | string;
  };
  year_price: {
    value: number | null;
    validators: Record<string, string>;
    error: null | string;
  };
  connections: {
    value: string[];
    validators: null;
    error: null | string;
  };
  permissions: {
    value: AuthPermission[];
    validators: Record<string, string>;
    error: null | string;
  };
  trial_period_days: {
    value: number;
    validators: Record<string, string>;
    error: null | string;
  };
}

export function createFormConfig(): CreateFormConfigType {
  return {
    plan_name: {
      value: '',
      validators: {
        required: '### is required.',
        textValidation:
          'Only lowercase letters, numbers and underscores (_) are allowed.',
      },
      error: null,
    },
    display_name: {
      value: '',
      validators: {
        required: '### is required.',
      },
      error: null,
    },
    month_price: {
      value: null,
      validators: {
        required: '### is required.',
        greaterThanPrice: 'Amount must be greater than 0.',
        lessThanPrice: 'Amount cannot exceed 999999.99.',
      },
      error: null,
    },
    year_price: {
      value: null,
      validators: {
        greaterThanPrice: 'Amount must be greater than or equal to 0.',
        lessThanPrice: 'Amount cannot exceed 999999.99.',
      },
      error: null,
    },
    connections: {
      value: [],
      validators: null,
      error: null,
    },
    permissions: {
      value: [],
      validators: {
        atleatOne: 'Atleast one permission should be enabled',
      },
      error: null,
    },
    trial_period_days: {
      value: 0,
      validators: {
        range: 'Trial days must be between 0 and 365.',
      },
      error: null,
    },
  };
}
